/* eslint-disable import/no-duplicates */
import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../../../services/api';

import { TIPO_USUARIO } from '../../../../utils/funcoes';

import { useToast } from '../../../../hooks/toast';
import { useClinica } from '../../../../hooks/clinica';
import { useAuth } from '../../../../hooks/auth';

import Input from '../../../../components/Input';
import Select from '../../../../components/Select';
import Loading from '../../../../components/Loading';
import PageInfo from '../../../../components/PageInfo';

import getValidationErrors from '../../../../utils/getValidationErrors';

import HeaderRelatorio from '../../HeaderRelatorio';
import getResponseErrors from '../../../../utils/getResponseErrors';
import Paginacao from '../../../../components/Paginacao';

interface IUsuariosNomeCodigo {
  UsuNome: string;
  UsuCodigo: Number;
}

interface IProdutividade extends IUsuariosNomeCodigo {
  totalAgendamentos: number;
  totalCobranca: number;
  totalCartao: number;
  totalRecebimentos: number;
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
  CliCodigo: string;
  UsuCodigo: string;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

interface IProdutividadeDetalheDTO {
  descricao: string;
  data: Date;
  dataFormatada: string;
  tipo: string;
}

interface IRetornoProdutividadeDetalhe {
  total: number;
  registros: IProdutividadeDetalheDTO[];
}

interface IPaginacao extends IFormBusca {
  limite: number;
  pagina: number;
}

const ProdutividadeDetalhe: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();
  const { optionsClinica } = useClinica();

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.ATENDENTE) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  function useQuery(): URLSearchParams {
    return new URLSearchParams(useLocation().search);
  }

  const query = useQuery();

  const initialData = useMemo(() => {
    let dataInicio = new Date();
    let dataFim = new Date();
    if (
      query.get('dataInicio') &&
      query.get('dataInicio')?.length === 10 &&
      query.get('dataFim') &&
      query.get('dataFim')?.length === 10
    ) {
      dataInicio = parseISO(`${query.get('dataInicio')} 03:00:00`);
      dataFim = parseISO(`${query.get('dataFim')} 03:00:00`);
    }

    if (query.get('CliCodigo'))
      formRefBusca.current?.setFieldValue('CliCodigo', query.get('CliCodigo'));

    return {
      dataInicio: format(dataInicio, 'yyyy-MM-dd'),
      dataFim: format(dataFim, 'yyyy-MM-dd'),
      CliCodigo: user.UsuClinica.toString(),
      UsuCodigo: query.get('UsuCodigo'),
    };
  }, [query, user.UsuClinica]);

  const informacoesPagina = `<b>Descrição:</b> Exibe o detalhemnto das operações que o colaborador selecionado realizou no sistema, quantos agendamentos, quantas operações com cartão saúde (inclusões, exclusões, alteração de vencimento entre outras), quantas cobranças foram realizadas e quantos recebimentos foram feitos.`;

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dadosProdutividade, setDadosProdutividade] = useState<
    IProdutividade[]
  >();
  const [dadosProdutividadeDetalhe, setDadosProdutividadeDetalhe] = useState<
    IProdutividadeDetalheDTO[]
  >();

  const [dataExibe, setDataExibe] = useState<IDataExibe>();
  const [clinica, setClinica] = useState('');
  const [nomeUsuario, setNomeUsuario] = useState<string>();
  const [paginacao, setPaginacao] = useState<IPaginacao>();
  const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
    undefined,
  );

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        if (!paginacao) return;
        const {
          dataInicio,
          dataFim,
          CliCodigo,
          UsuCodigo,
          limite,
          pagina,
        } = paginacao;

        const dataInicioFormatada = format(dataInicio, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFim, 'yyyy-MM-dd');

        const filtraUsuario = `&UsuCodigo=${UsuCodigo}`;
        const filtraClinica = CliCodigo ? `&CliCodigo=${CliCodigo}` : '';

        const response = await api.get<IRetornoProdutividadeDetalhe>(
          `/relatorios/produtividadedetalhe?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}${filtraUsuario}${filtraClinica}&limite=${limite}&pagina=${pagina}`,
        );

        setDadosProdutividadeDetalhe(undefined);

        const produtividadeDetalhe = response.data.registros.map(pro => {
          return {
            ...pro,
            dataFormatada: format(
              parseISO(pro.data.toString()),
              'dd/MM/yyyy HH:mm',
            ),
          };
        });

        setDadosProdutividadeDetalhe(produtividadeDetalhe);
        setTotalRegistros(
          response.data.total === 0 ? undefined : response.data.total,
        );
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível carregar dados',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    }

    if (paginacao) loadDados();
  }, [addToast, paginacao]);

  useEffect(() => {
    if (optionsClinica.length > 0 && user) formRefBusca.current?.submitForm();
  }, [optionsClinica.length, user]);

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
          CliCodigo: Yup.string().nullable(true),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        setClinica(
          optionsClinica.find(cli => cli.value === dados.CliCodigo)?.label ||
            '',
        );

        const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
        const dataFimSelecionada = parseISO(dados.dataFim.toString());

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        const filtraUsuario = `&UsuCodigo=${initialData.UsuCodigo}`;
        const filtraClinica = dados.CliCodigo
          ? `&CliCodigo=${dados.CliCodigo}`
          : '';

        const response = await api.get<IProdutividade[]>(
          `/relatorios/produtividade?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}${filtraUsuario}${filtraClinica}`,
        );

        setDataExibe({
          dataDiaSemana: `${format(
            dataInicioSelecionada,
            'dd/MM/yyyy',
          )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')}`,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
        });

        setDadosProdutividade(response.data);
        setNomeUsuario(response.data[0].UsuNome);

        setPaginacao({
          dataInicio: dataInicioSelecionada,
          dataFim: dataFimSelecionada,
          CliCodigo: dados.CliCodigo,
          UsuCodigo: initialData.UsuCodigo || '',
          pagina: 1,
          limite: 50,
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, optionsClinica, initialData.UsuCodigo],
  );

  const handleProximaPagina = useCallback((page: number) => {
    setPaginacao(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      <>
        <div className="d-print-none bg-white border p-2 mb-2">
          <div className="p-2 mb-2 d-flex justify-content-between">
            <h2 id="topoBusca">Produtividade</h2>{' '}
            <PageInfo
              descricao={informacoesPagina}
              permissao={TIPO_USUARIO.SUPERVISOR}
            />
          </div>
          {optionsClinica.length > 0 && user && (
            <Form
              ref={formRefBusca}
              initialData={initialData}
              onSubmit={handleSubmitBusca}
            >
              <Row>
                <Col md={3}>
                  <Input name="dataInicio" label="De" type="date" required />
                </Col>
                <Col md={3}>
                  <Input name="dataFim" label="Até" type="date" required />
                </Col>
                <Col md={3}>
                  <Select
                    name="CliCodigo"
                    label="Clinica"
                    options={optionsClinica}
                    disabled={user.UsuTipo !== TIPO_USUARIO.ADMINISTRADOR}
                  />
                </Col>
                <Col md={3}>
                  <br />
                  <Button type="submit" size="sm" variant="outline-secondary">
                    BUSCAR
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </div>

        <HeaderRelatorio
          dataImpressao={dataExibe?.dataHora}
          titulo={`PRODUTIVIDADE ${
            clinica.length > 0 ? `- ${clinica} -` : '-'
          } ${nomeUsuario ? ` ${nomeUsuario} -` : ''} ${
            dataExibe?.dataDiaSemana || ''
          }`}
        />

        <div className="bg-white border p-2">
          {dadosProdutividade && (
            <>
              <table
                className="table table-hover table-sm table-font-small table-bordered"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="text-center">
                    <th>Colaborador</th>
                    <th>Agendamentos</th>
                    <th>Cartão Saúde</th>
                    <th>Cobrança</th>
                    <th>Lanç. Financeiros</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosProdutividade &&
                    dadosProdutividade.map(rel => (
                      <tr
                        key={`usua-${rel.UsuCodigo}`}
                        style={{ fontSize: '.7rem', height: '30px' }}
                      >
                        <td>{rel.UsuNome}</td>
                        <td>{rel.totalAgendamentos}</td>
                        <td>{rel.totalCartao}</td>
                        <td>{rel.totalCobranca}</td>
                        <td>{rel.totalRecebimentos}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </>
          )}

          {dadosProdutividadeDetalhe && (
            <>
              <h5 style={{ textAlign: 'center', fontSize: '1rem' }}>
                Detalhamento - Total de {totalRegistros} registros
              </h5>
              <table
                className="table table-hover table-sm table-font-small table-bordered"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="text-center">
                    <th>Data</th>
                    <th>Tipo</th>
                    <th>Descrição</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosProdutividadeDetalhe &&
                    dadosProdutividadeDetalhe.map(rel => (
                      <tr
                        key={`usuaDet-${rel.data}${rel.tipo}${rel.descricao}`}
                        style={{ fontSize: '.7rem', height: '30px' }}
                      >
                        <td>{rel.dataFormatada}</td>
                        <td>{rel.tipo}</td>
                        <td>{rel.descricao}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
              <Paginacao
                limitePagina={paginacao?.limite || 50}
                paginasVizinhas={2}
                totalRegistros={totalRegistros}
                paginaInicio={paginacao?.pagina}
                handleProximaPagina={handleProximaPagina}
              />
            </>
          )}
        </div>
      </>
    </Container>
  );
};

export default ProdutividadeDetalhe;
